/**
 * WhatsNew Popup
 *
 * Modal overlay showing release notes to users after a new version is published.
 * Uses createPortal to render on top of everything.
 */

import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

interface WhatsNewPopupProps {
  version: string;
  title: string;
  features: Array<{ title: string; description: string }>;
  onDismiss: () => void;
}

export function WhatsNewPopup({ version, title, features, onDismiss }: WhatsNewPopupProps) {
  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onDismiss();
      }}
    >
      <div className="relative bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 p-6">
        {/* Close button */}
        <button
          onClick={onDismiss}
          className="absolute top-4 right-4 p-1 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Version badge */}
        <div className="mb-4">
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
            v{version}
          </span>
        </div>

        {/* Title */}
        <h2 className="text-xl font-bold text-gray-900 mb-4">{title}</h2>

        {/* Features list */}
        {features.length > 0 && (
          <ul className="space-y-3 mb-6">
            {features.map((feature, index) => (
              <li key={index} className="flex items-start gap-3">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-purple-500 flex-shrink-0" />
                <div>
                  <span className="text-sm font-semibold text-gray-900">{feature.title}</span>
                  {feature.description && (
                    <p className="text-sm text-gray-500 mt-0.5">{feature.description}</p>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}

        {/* Dismiss button */}
        <button
          onClick={onDismiss}
          className="w-full px-4 py-2.5 bg-mazo-600 text-white rounded-lg hover:bg-mazo-700 transition-colors font-medium text-sm"
        >
          Got it!
        </button>
      </div>
    </div>,
    document.body
  );
}
